import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWs } from "@/context/WsContext";
import {
  fetchConversations, fetchThread, sendMessage, fetchUser,
  markThreadRead, fetchPresence, uploadFile, fileUrl, api,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Wifi, WifiOff, Paperclip, FileText, Check, CheckCheck, X } from "lucide-react";

const TYPING_TIMEOUT = 3000;

export default function MessagesPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { otherId } = useParams();
  const { connected, subscribe, wsRef } = useWs();
  const [convos, setConvos] = useState([]);
  const [thread, setThread] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [presence, setPresence] = useState({}); // { user_id: bool }
  const [otherTyping, setOtherTyping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachment, setAttachment] = useState(null); // { path, name }
  const bottomRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const typingSentRef = useRef(false);
  const otherTypingTimerRef = useRef(null);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);

  const loadConvos = useCallback(async () => {
    try {
      const c = await fetchConversations();
      setConvos(c);
      // Poll presence for all contacts
      const ids = c.map((x) => x.other_id);
      if (ids.length) {
        try {
          const pres = await fetchPresence(ids);
          setPresence(pres);
        } catch {}
      }
    } catch {}
  }, []);
  useEffect(() => { if (user) loadConvos(); }, [user, loadConvos]);

  const loadThread = useCallback(async () => {
    if (!otherId) { setThread([]); setOther(null); return; }
    try {
      const [t, u] = await Promise.all([fetchThread(otherId), fetchUser(otherId)]);
      setThread(t);
      setOther(u);
      // Mark thread as read (server pushes 'read' event to sender)
      markThreadRead(otherId).catch(() => {});
      // Fetch presence of the specific user
      try {
        const p = await fetchPresence([otherId]);
        setPresence((prev) => ({ ...prev, ...p }));
      } catch {}
    } catch {}
  }, [otherId]);
  useEffect(() => { loadThread(); }, [loadThread]);

  // WebSocket subscribe: message, read, typing, presence
  useEffect(() => {
    const unsub = subscribe((data) => {
      if (data.type === "message") {
        const m = data.message;
        if (otherId && (m.sender_id === otherId || m.recipient_id === otherId)) {
          setThread((t) => t.some((x) => x.message_id === m.message_id) ? t : [...t, m]);
          // If this is incoming, mark thread read
          if (m.sender_id === otherId) markThreadRead(otherId).catch(() => {});
        }
        loadConvos();
      } else if (data.type === "read") {
        // My sent messages to reader_id are read
        if (data.conversation_id) {
          setThread((t) => t.map((x) => (x.conversation_id === data.conversation_id && x.sender_id === user?.user_id) ? { ...x, read: true, read_at: data.read_at } : x));
        }
      } else if (data.type === "typing") {
        if (data.from === otherId) {
          setOtherTyping(!!data.typing);
          clearTimeout(otherTypingTimerRef.current);
          if (data.typing) {
            otherTypingTimerRef.current = setTimeout(() => setOtherTyping(false), TYPING_TIMEOUT + 500);
          }
        }
      } else if (data.type === "presence") {
        setPresence((p) => ({ ...p, [data.user_id]: data.online }));
      }
    });
    return unsub;
  }, [otherId, subscribe, user, loadConvos]);

  useEffect(() => {
    const box = bottomRef.current?.parentElement;
    if (box) box.scrollTo({ top: box.scrollHeight, behavior: "smooth" });
  }, [thread, otherTyping]);

  const sendTyping = (typing) => {
    if (!otherId) return;
    const ws = wsRef?.current;
    if (!ws || ws.readyState !== 1) return;
    try {
      ws.send(JSON.stringify({ type: "typing", to: otherId, typing }));
    } catch {}
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
    if (!typingSentRef.current) {
      sendTyping(true);
      typingSentRef.current = true;
    }
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      sendTyping(false);
      typingSentRef.current = false;
    }, TYPING_TIMEOUT);
  };

  const handleAttach = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadFile(file);
      setAttachment({ path: r.path, name: file.name });
      toast.success("Attached — send to share");
    } catch {
      toast.error("Upload failed (only images supported for now)");
    } finally { setUploading(false); }
  };

  const send = async (e) => {
    e?.preventDefault();
    if ((!text.trim() && !attachment) || !otherId) return;
    try {
      // Clear typing indicator
      sendTyping(false);
      typingSentRef.current = false;
      clearTimeout(typingTimeoutRef.current);

      const payload = { recipient_id: otherId, text };
      if (attachment) {
        payload.attachment_path = attachment.path;
        payload.attachment_name = attachment.name;
      }
      const m = await api.post("/messages", payload).then((r) => r.data);
      setText("");
      setAttachment(null);
      setThread((t) => t.some((x) => x.message_id === m.message_id) ? t : [...t, m]);
      loadConvos();
    } catch { toast.error("Failed to send"); }
  };

  if (loading || !user) return <div className="p-16 text-center">Loading…</div>;

  const isOther = (id) => presence[id];

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-primary">◆ INBOX</div>
        <div className={`flex items-center gap-1.5 mono text-xs ${connected ? "text-green-700" : "text-muted-foreground"}`} data-testid="ws-status">
          {connected ? <Wifi className="w-3.5 h-3.5"/> : <WifiOff className="w-3.5 h-3.5"/>}
          {connected ? "LIVE" : "CONNECTING"}
        </div>
      </div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Messages</h1>

      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] border border-foreground/15 min-h-[500px]">
        <div className="border-r border-foreground/15 max-h-[70vh] overflow-y-auto">
          {convos.length === 0 && <div className="p-6 text-sm text-muted-foreground">No conversations yet.</div>}
          {convos.map((c) => (
            <button
              key={c.conversation_id}
              data-testid={`convo-${c.other_id}`}
              onClick={() => navigate(`/messages/${c.other_id}`)}
              className={`w-full text-left p-4 border-b border-foreground/10 hover:bg-muted flex items-center gap-3 ${otherId === c.other_id ? "bg-muted" : ""}`}
            >
              <div className="relative">
                {c.other_picture ? (
                  <img src={c.other_picture} className="w-10 h-10 rounded-full object-cover border border-foreground/20" alt=""/>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">{c.other_name?.[0]}</div>
                )}
                {isOther(c.other_id) && (
                  <span data-testid={`presence-dot-${c.other_id}`} className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-background rounded-full"/>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{c.other_name}</div>
                <div className="text-xs text-muted-foreground truncate">{c.last_message || "Attachment"}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="flex flex-col max-h-[70vh]">
          {!otherId ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              Select a conversation to start
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-foreground/15 flex items-center gap-3">
                <div className="relative">
                  {other?.picture ? (
                    <img src={other.picture} className="w-9 h-9 rounded-full object-cover" alt=""/>
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center">{other?.name?.[0] || "?"}</div>
                  )}
                  {isOther(otherId) && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 border-2 border-background rounded-full"/>
                  )}
                </div>
                <div>
                  <div className="font-medium">{other?.name || "…"}</div>
                  <div className="text-xs text-muted-foreground mono">{isOther(otherId) ? "ONLINE" : "OFFLINE"}</div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {thread.map((m) => {
                  const mine = m.sender_id === user.user_id;
                  return (
                    <div key={m.message_id} data-testid={`msg-${m.message_id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] ${mine ? "items-end" : "items-start"} flex flex-col gap-1`}>
                        <div className={`px-4 py-2.5 ${mine ? "bg-foreground text-background" : "bg-muted"}`}>
                          {m.attachment_path && (
                            <a
                              href={fileUrl(m.attachment_path)}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`attach-${m.message_id}`}
                              className={`flex items-center gap-2 text-sm mb-1 underline decoration-dotted ${mine ? "text-background" : ""}`}
                            >
                              <FileText className="w-4 h-4"/> {m.attachment_name || "attachment"}
                            </a>
                          )}
                          {m.text && <div className="text-sm whitespace-pre-wrap">{m.text}</div>}
                        </div>
                        <div className="flex items-center gap-1 px-1 text-[10px] mono text-muted-foreground">
                          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          {mine && (
                            m.read
                              ? <CheckCheck data-testid={`read-${m.message_id}`} className="w-3.5 h-3.5 text-primary"/>
                              : <Check className="w-3.5 h-3.5"/>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {otherTyping && (
                  <div data-testid="typing-indicator" className="flex justify-start">
                    <div className="bg-muted px-4 py-2.5 inline-flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-foreground/60 rounded-full animate-bounce" style={{animationDelay:"0ms"}}/>
                      <span className="w-1.5 h-1.5 bg-foreground/60 rounded-full animate-bounce" style={{animationDelay:"150ms"}}/>
                      <span className="w-1.5 h-1.5 bg-foreground/60 rounded-full animate-bounce" style={{animationDelay:"300ms"}}/>
                    </div>
                  </div>
                )}
                <div ref={bottomRef}/>
              </div>

              {attachment && (
                <div className="px-3 py-2 border-t border-foreground/10 bg-muted flex items-center justify-between text-sm" data-testid="attachment-preview">
                  <div className="flex items-center gap-2 min-w-0"><FileText className="w-3.5 h-3.5 flex-shrink-0"/> <span className="truncate">{attachment.name}</span></div>
                  <button onClick={() => setAttachment(null)} className="hover:text-destructive"><X className="w-4 h-4"/></button>
                </div>
              )}
              <form onSubmit={send} className="p-3 border-t border-foreground/15 flex gap-2 items-center">
                <label className="border border-foreground/20 p-2 hover:bg-muted cursor-pointer" data-testid="attach-label">
                  <Paperclip className="w-4 h-4"/>
                  <input data-testid="attach-input" type="file" hidden onChange={handleAttach} disabled={uploading}/>
                </label>
                <Input
                  data-testid="msg-input"
                  value={text}
                  onChange={handleTextChange}
                  placeholder={uploading ? "Uploading attachment…" : "Type a message…"}
                  className="rounded-none border-foreground/20 flex-1"
                  disabled={uploading}
                />
                <button
                  data-testid="msg-send-btn"
                  type="submit"
                  disabled={(!text.trim() && !attachment) || uploading}
                  className="bg-primary text-primary-foreground px-4 py-2 mono text-xs disabled:opacity-40 flex items-center gap-2"
                >
                  <Send className="w-3.5 h-3.5"/> SEND
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
